import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { OverviewTable } from "@/components/admin/subscriptions/OverviewTable";
import { PendingBankTransferTable } from "@/components/admin/subscriptions/PendingBankTransferTable";
import { LedgerTable } from "@/components/admin/subscriptions/LedgerTable";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { GearSix } from "@phosphor-icons/react";

export const Route = createFileRoute("/dashboard/admin/subscriptions/")({
  component: AdminSubscriptions,
});

type Tab = "overview" | "pending" | "ledger";

function AdminSubscriptions() {
  const [tab, setTab] = useState<Tab>("overview");

  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["admin", "subscription-ledger", "pending-count"],
    queryFn: async () => {
      const res = await fetch(
        "/api/admin/subscription-ledger?status=pending&paymentMethod=bank_transfer",
      );
      if (!res.ok) return 0;
      const body = (await res.json()) as { rows: unknown[] };
      return body.rows.length;
    },
    refetchInterval: 60_000,
  });

  const tabOptions: { value: Tab; label: string }[] = [
    { value: "overview", label: "Pregled terapeuta" },
    {
      value: "pending",
      label: pendingCount > 0 ? `Uplatnice (${pendingCount})` : "Uplatnice",
    },
    { value: "ledger", label: "Sve uplate" },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-14">
      {/* Header */}
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Pretplate terapeuta
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Status pretplata, uplatnice na čekanju i evidencija svih uplata.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Link
            to="/dashboard/admin/settings"
            search={{ section: "billing" }}
            className="group inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-primary/25 bg-card pl-2 pr-4 text-sm font-medium text-primary shadow-sm transition-all duration-200 hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-md hover:shadow-primary/25 active:scale-[0.97] sm:order-2"
          >
            <span className="flex size-6 items-center justify-center rounded-lg bg-primary/10 transition-all duration-200 group-hover:rotate-45 group-hover:bg-white/20">
              <GearSix className="size-4" weight="bold" />
            </span>
            Konfiguracija naplate
          </Link>
          <SegmentedFilter
            ariaLabel="Prikaz pretplata"
            options={tabOptions}
            value={tab}
            onChange={setTab}
            className="sm:order-1"
          />
        </div>
      </header>

      {/* Tab content */}
      {tab === "overview" && <OverviewTable />}
      {tab === "pending" && <PendingBankTransferTable />}
      {tab === "ledger" && <LedgerTable />}
    </div>
  );
}
